/**
 * facilityProtection.js - 2026-10-21 (R2-03, Dr. Tatar: exposure-weighted protection of the medical facilities)
 *
 * The medical protection level (Tab 2, 0 to 100 percent; Gielerak and Murawski 2024, safety coefficient bo) used to be turned into a flat capacity factor
 * 0.5 + 0.5 x bo that never reached the surgical capacity of the resource model (the slider changed nothing). Now the capacity lost to enemy fire depends
 * on how exposed the facilities are:
 *
 *   capacity factor = 1 - P_HIT x DOWNTIME_DAYS x (1 - protection / 100)
 *   P_HIT          = mtf_damage_probability of the artillery threat of Tab 2 (per day per facility: moderate 0.05, high 0.10, extreme 0.20; low: none in the table = 0)
 *   DOWNTIME_DAYS  = 3 days a hit facility is out (displacement, repair, set-up: casualtyCalculatorConstants set-up times 12 to 48 h). EDUCATED GUESS.
 * At the default protection of 70 percent a high artillery threat gives 0.91 and an extreme one 0.82 (the former flat factor was 0.85 at the default).
 * Drone strikes on facilities have no probability in the threat table and are not included.
 */
import { ARTILLERY_THREAT } from '../../constants/threatAssessment.js';

export const FACILITY_PROTECTION_ON = true;   // false = the protection level does not change the capacity (as before)
export const FACILITY_DOWNTIME_DAYS = 3;
export const FACILITY_PROTECTION_STATUS = Object.freeze({
  status: 'EDUCATED_GUESS',
  basis: 'Hit probability per day from the artillery threat table (threatAssessment.js); downtime of 3 days is a guess; protection level is the planner input (Gielerak and Murawski 2024).'
});

/**
 * @param {Object} c protectionLevel (0 to 100), artilleryLevel (key of ARTILLERY_THREAT, default LOW)
 * @returns {{factor:number, p_hit:number}}
 */
export function protectionCapacityFactor({ protectionLevel, artilleryLevel }) {
  if (typeof protectionLevel !== 'number' || Number.isNaN(protectionLevel) || protectionLevel < 0 || protectionLevel > 100) {
    throw new Error(`facilityProtection: protection level must be a number from 0 to 100 (got: ${protectionLevel})`);
  }
  const key = artilleryLevel || 'LOW';
  const row = ARTILLERY_THREAT[key];
  if (!row) throw new Error(`facilityProtection: unknown artillery threat '${key}' (valid: ${Object.keys(ARTILLERY_THREAT).join(', ')})`);
  const pHit = typeof row.mtf_damage_probability === 'number' ? row.mtf_damage_probability : 0;
  const loss = Math.min(1, pHit * FACILITY_DOWNTIME_DAYS * (1 - protectionLevel / 100));
  return { factor: FACILITY_PROTECTION_ON ? 1 - loss : 1, p_hit: pHit };
}
